import { useField } from "formik";
import classnames from "classnames";
import { memo } from "react";

import { Input, IInputProps } from "components/Input";
import { Label } from "components/Label";

interface IProps extends Omit<IInputProps, "customRef"> {
  classNames?: {
    input?: string;
    error?: string;
    icon?: string;
    label?: string;
  };
  className?: string;
  withErrorMessage?: boolean;
  customRef?: React.MutableRefObject<
    HTMLInputElement | null | HTMLTextAreaElement
  >;
  name: string;
  label?: string;
  require?: boolean;
  required?: boolean;
  icon?: string;
  customLabel?: React.ReactNode;
  iconComponent?: React.ReactNode;
  leftInputElement?: React.ReactNode;
  rightInputElement?: React.ReactNode;
}

function FormikInput({
  classNames,
  name,
  className,
  label,
  withErrorMessage = true,
  require,
  icon,
  required,
  customLabel,
  iconComponent,
  leftInputElement,
  rightInputElement,
  ...rest
}: IProps) {
  const [field, form] = useField(name);
  const message = form.touched && form.error;
  const showError = withErrorMessage && !!message;
  return (
    <div
      className={classnames(
        "formik-input flex flex-col relative overflow-hidden",
        className,
        {
          "pb-4": !showError,
        }
      )}
    >
      {customLabel}
      {!customLabel && label && (
        <Label
          name={label}
          className={classNames?.label}
          required={require || required}
        />
      )}
      <div className="flex items-center h-full rounded-md border-primary hover:border-primaryBlue border overflow-hidden max-h-[38px]">
        {icon && !iconComponent && (
          <div
            className={classnames(
              classNames?.icon,
              "w-[40px] p-3 bg-gray-200  flex justify-center h-full"
            )}
          >
            <img src={icon} alt="input-icon" />
          </div>
        )}
        {iconComponent}
        {leftInputElement}
        {rightInputElement}
        <Input
          className={classnames(
            classNames?.input,
            "text-primary text-[12px] pl-[12px] bg-primary p-3 px-5 w-full h-full max-h-[38px]",
            { "bg-disabled": rest.disabled }
          )}
          {...field}
          {...rest}
          customRef={
            rest.customRef as React.MutableRefObject<HTMLInputElement | null>
          }
        />
      </div>
      {showError && (
        <div className={classnames(classNames?.error, "text-xs text-red-400")}>
          {message}
        </div>
      )}
    </div>
  );
}

export default memo(FormikInput);
